// apps/web/src/pages/author/sections/CycleErrorBanner.tsx
//
// Phase 9 Plan 10 Wave-4 — destructive role="alert" banner that surfaces
// cycle detection from the evaluator output (CSTR-03).
//
// UI-SPEC §C lines 304-318 contract:
//   role="alert" aria-live="assertive"
//   bg-destructive/10 border border-destructive/30 text-destructive
//   px-6 py-4 rounded-md
//
// Title: "Constraint cycle detected"
// Body : "Constraints depend on each other in a loop: {chain}. Break the
//         cycle by removing one of the dependencies."
//   {chain} renders the cycle.chain as <button> elements separated by " → "
//   arrows; clicking calls onLocateConstraint(idx) — idx parsed from the
//   "constraint-N" string. The host (Constraints section) scrolls + focuses
//   the matching ConstraintRow.
//
// Multiple cycles → render multiple banners stacked. Empty cycles → render
// nothing (null). The banner does NOT auto-dismiss — it stays visible until
// the cycle is resolved.
//
// XSS posture (T-09-10-03): the only strings rendered into the banner come
// from `cycle.chain` (always `constraint-${index}` per Wave 1
// cycle-detection.ts) and static title/body copy. The component never
// renders `cycle.explanation` or any user-authored text — React's default
// text escape covers the safe constraint-id shape.

import * as React from "react";
import type { CycleError } from "@the-recipe-spread/shared";

export interface CycleErrorBannerProps {
  /** Evaluator cycle output. Empty array → component renders null. */
  cycles: ReadonlyArray<CycleError>;
  /** Host callback invoked with the document position of the clicked
   * constraint id. The host scrolls + focuses the matching row. */
  onLocateConstraint?: (index: number) => void;
}

// Parse the trailing index off "constraint-N" — returns NaN on malformed
// input (defensive; should not happen given Wave 1 evaluator output shape).
function parseConstraintIndex(id: string): number {
  const match = /^constraint-(\d+)$/.exec(id);
  if (match === null) return Number.NaN;
  return Number.parseInt(match[1] as string, 10);
}

export function CycleErrorBanner({
  cycles,
  onLocateConstraint,
}: CycleErrorBannerProps): React.ReactElement | null {
  if (cycles.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {cycles.map((cycle, cycleIdx) => (
        <div
          // Cycle chain text is stable for the duration of this evaluation
          // pass; combined with the cycleIdx it forms a stable key across
          // the cycles[] array.
          key={`cycle-${cycleIdx}-${cycle.chainText}`}
          role="alert"
          aria-live="assertive"
          className="bg-destructive/10 border border-destructive/30 text-destructive px-6 py-4 rounded-md"
        >
          <h3 className="font-semibold mb-1">Constraint cycle detected</h3>
          <p className="text-sm">
            Constraints depend on each other in a loop:{" "}
            {cycle.chain.map((id, i, arr) => (
              <React.Fragment key={`${id}-${i}`}>
                <button
                  type="button"
                  onClick={() => {
                    if (onLocateConstraint === undefined) return;
                    const idx = parseConstraintIndex(id);
                    if (Number.isNaN(idx)) return;
                    onLocateConstraint(idx);
                  }}
                  className="underline underline-offset-2 text-destructive hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive rounded-sm"
                >
                  {id}
                </button>
                {i < arr.length - 1 ? <span> → </span> : null}
              </React.Fragment>
            ))}
            . Break the cycle by removing one of the dependencies.
          </p>
        </div>
      ))}
    </div>
  );
}
CycleErrorBanner.displayName = "CycleErrorBanner";
